import debounce from 'just-debounce';
import VanillaModal from 'vanilla-modal';
import {
  trapFocus,
  removeTrapFocus,
} from '@shopify/theme-a11y';
import EventHandler from '@pixelunion/events';

import ScrollLock from '../helpers/ScrollLock';

export default class Modal {
  constructor(callbacks = {}) {
    this.body = document.body;

    this.modal = null;
    this.modalEl = document.querySelector('[data-modal-container]');
    this.modalInner = this.modalEl.querySelector('[data-modal-inner]');

    this.trigger = null;

    this.events = new EventHandler();

    // Extend default vanilla-modal callbacks back to instantiator of Modal
    this.defaultCallbacks = {
      onOpen: () => {},
      onClose: () => {},
      onBeforeOpen: () => {},
      onBeforeClose: () => {},
    };

    this.callbacks = { ...this.defaultCallbacks, ...callbacks };

    this._onOpen = this._onOpen.bind(this);
    this._onBeforeOpen = this._onBeforeOpen.bind(this);
    this._onClose = this._onClose.bind(this);
    this._onBeforeClose = this._onBeforeClose.bind(this);
    this._closeEsc = this._closeEsc.bind(this);

    this.position = this.position.bind(this);

    this.modalOptions = {
      loadClass: '',
      class: 'modal-loaded',
      onOpen: this._onOpen,
      onClose: this._onClose,
      onBeforeOpen: this._onBeforeOpen,
      onBeforeClose: this._onBeforeClose,
      transitions: false,
    };
  }

  unload() {
    if (!this.modal || !this.modal.isOpen) return;

    ScrollLock.unlock();
    this.events.unregisterAll();
    this.modal.destroy();
  }

  /**
   * Open a modal with contents from selector
   *
   * @param selector
   * @param handle
   */
  open(selector, handle = 'general', trigger = null) {
    this.modal = new VanillaModal(this.modalOptions);
    this.modal.open(selector);
    this._addModalClass(handle);
    this.trigger = trigger;
    this.events.register(window, 'keydown', this.closeEsc);
  }

  close() {
    this.modal.close();
  }

  _closeEsc(e) {
    if (e.key === 'Escape') {
      this.close();
    }
  }

  isOpen() {
    return this.modal && this.modal.isOpen;
  }

  /**
   * Update the vertical positioning of modal
   */
  position() {
    const windowHeight = window.innerHeight;

    const modalHeight = this.modalInner.getBoundingClientRect().height;
    const modalPadding = parseInt(window.getComputedStyle(this.modalEl).paddingTop, 10) * 2;

    const offset = (windowHeight - modalPadding - modalHeight) / 2;
    this.modalInner.style.marginTop = offset > 0 ? `${offset}px` : 0;
  }

  /**
   * Add a class to the modal for individual styling
   * @param handle
   * @private
   */
  _addModalClass(handle) {
    this.modalEl.classList.add(`modal--${handle}`);
  }

  /**
   * Remove modal class based on the handle
   * @private
   */
  _removeModalClass() {
    const modalClass = this.modalEl.className.match(/modal--[\w-]*\b/);
    if (!modalClass) {
      return;
    }

    this.modalEl.classList.remove(modalClass[0]);
  }

  _onClose() {
    this._removeModalClass();
    ScrollLock.unlock();
    this.body.classList.remove('modal-visible');

    this.events.unregisterAll();

    this.modalInner.style.marginTop = '';

    this.callbacks.onClose();

    removeTrapFocus(this.modalEl);

    if (this.activeElement) {
      this.activeElement.focus();
    }
  }

  _onOpen() {
    this.activeElement = document.activeElement;
    this.position();
    ScrollLock.lock(this.modalEl);
    this.body.classList.add('modal-visible');
    this.events.register(window, 'resize', debounce(() => this.position(), 16, true, true));

    this.callbacks.onOpen();

    trapFocus(this.modalEl);
  }

  _onBeforeClose() {
    this.callbacks.onBeforeClose();
  }

  _onBeforeOpen() {
    this.callbacks.onBeforeOpen();
  }
}
